import dayjs from "dayjs";
import { useCallback } from "react";
import { useRouter } from "solito/router";
import { ChevronRight } from "@tamagui/lucide-icons";
import type { GetIssuesWithActionsQuery, MostRecentIssueWithClosedEventWithoutFeedbackQuery } from "@medbillai/graphql-types";
import { issue__EventType } from "@medbillai/graphql-types";
import { Circle, OutlinedButton, Paragraph, Paragraph2, ParagraphSmall, View, XStack, type XStackProps, YStack } from "@medbillai/ui";
import type { CaseType } from "@medbillai/schemas";
import { issueUrl } from "@medbillai/utils";
import { BillHelpBadge, CoverageCheckBadge, SomethingElseBadge } from "../../../components/local-images";
import { analyticsService } from "../../../lib/analytics";
type IssuesArray = NonNullable<NonNullable<GetIssuesWithActionsQuery["me"]>["issues"]>;
type Issue = IssuesArray[number];
type Action = NonNullable<Issue["actions"]>[number];
type ClosedEvent = NonNullable<MostRecentIssueWithClosedEventWithoutFeedbackQuery["issue__ClosedEvent"]>[number];
type ResolvedIssue = ClosedEvent["issue"];
type IssueSummaryIssue = Issue | ResolvedIssue;
interface IssueSummaryProps {
  issue: IssueSummaryIssue;
  stackProps?: XStackProps;
}
const CURRENCY_FORMATTER = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
});
const CASE_TYPE_BADGES: Partial<Record<CaseType, React.JSX.Element>> = {
  BILL_ISSUE: <BillHelpBadge />,
  BENEFITS_QUESTION: <CoverageCheckBadge />,
  OTHER: <SomethingElseBadge />
};
const getActionLabel = (action: Action): string => {
  const eventTypeToLabel: Record<issue__EventType, string> = {
    [issue__EventType.CHAT]: action.actionRequired ? "Respond to request" : "Review new message",
    [issue__EventType.PAYMENT_AGREEMENT_REQUESTED]: "Review payment agreement",
    [issue__EventType.TEMPLATE_RELEASE_REQUESTED]: "Sign HIPAA form",
    [issue__EventType.BENEFITS_SUMMARY_GENERATED]: "Review benefits summary",
    [issue__EventType.BILL_DISPUTE]: "Review your case",
    [issue__EventType.CLOSED]: "Review your case",
    [issue__EventType.CLOSURE_PAYMENT_REQUESTED]: "Make a payment",
    [issue__EventType.COMMENT]: "Review your case",
    [issue__EventType.CONNECT_INSURANCE_REQUESTED]: "Connect your insurance",
    [issue__EventType.CONNECT_ACCOUNT_REQUESTED]: "Connect your account",
    [issue__EventType.DOCUMENT_UPLOADED]: "Review documents",
    [issue__EventType.HIPAA_RELEASE_REQUESTED]: "Sign HIPAA form",
    [issue__EventType.INTERNAL_DOCUMENT_UPLOADED]: "Review your case",
    [issue__EventType.PLAN_SELECTION]: "Review plans",
    [issue__EventType.REOPENED]: "Review your case",
    [issue__EventType.USER_SELECTION_REQUESTED]: "Make a selection",
    [issue__EventType.USER_MULTI_SELECTION_REQUESTED]: "Make a selection",
    [issue__EventType.BILL_PAYMENT_REQUESTED]: "Pay your bill",
    [issue__EventType.BILL_PAYMENT_SUCCESS]: "Payment completed",
    [issue__EventType.DOCUMENT_ANALYSIS_COMPLETE]: "Review your case",
    [issue__EventType.DOCUMENT_ANALYSIS_SELECT_VISIT]: "Review your case",
    [issue__EventType.DOCUMENT_ANALYSIS_STARTED]: "Review your case",
    [issue__EventType.DOCUMENT_ANALYSIS]: "Review your case",
    [issue__EventType.POST_ESCALATION_ACTIONS]: "Complete required steps",
    [issue__EventType.BILL_PAY_REQUESTED]: "Review bill pay agreement",
    [issue__EventType.BILL_PAY_SUCCESS]: "Payment completed",
    [issue__EventType.TOOL_EXECUTION]: "Review your case"
  };
  return eventTypeToLabel[action.eventType] ?? "Review your case";
};
function hasActions(issue: IssueSummaryIssue): issue is Issue {
  return "actions" in issue;
}
function hasAmounts(issue: IssueSummaryIssue): issue is ResolvedIssue {
  return "totalBillDollarAmount" in issue || "dollarAmountSaved" in issue;
}
export function IssueSummary({
  issue,
  stackProps
}: IssueSummaryProps) {
  const {
    push
  } = useRouter();
  const handlePress = useCallback(() => {
    analyticsService.trackEvent("IssueActionTapped", {
      caseType: issue.caseType ?? "UNKNOWN",
      source: "ISSUE_ROW"
    });
    push(issueUrl({
      issueId: issue.id,
      unread: false,
      title: issue.title
    }));
  }, [push, issue.id, issue.caseType, issue.title]);
  const handleActionPress = useCallback((actionType: string) => () => {
    analyticsService.trackEvent("IssueActionTapped", {
      caseType: issue.caseType ?? "UNKNOWN",
      source: "ACTION_BUTTON",
      actionType
    });
    push(issueUrl({
      issueId: issue.id,
      unread: false,
      title: issue.title
    }));
  }, [push, issue.id, issue.caseType, issue.title]);
  const formattedDate = dayjs(issue.openedAt).format("ddd, MMM D");
  const badge = CASE_TYPE_BADGES[issue.caseType as CaseType] ?? <SomethingElseBadge />;
  return <XStack gap="$3" paddingVertical="$4" alignItems={hasActions(issue) ? "flex-start" : "center"} onPress={handlePress} cursor="pointer" pressStyle={{
    backgroundColor: "$surface1Hover"
  }} hoverStyle={{
    backgroundColor: "$surface1Hover"
  }} {...stackProps} data-sentry-element="XStack" data-sentry-component="IssueSummary" data-sentry-source-file="IssueSummary.tsx">
      {badge}

      <XStack flex={1} data-sentry-element="XStack" data-sentry-source-file="IssueSummary.tsx">
        <YStack flex={1} gap="$0" alignItems="flex-start" data-sentry-element="YStack" data-sentry-source-file="IssueSummary.tsx">
          <Paragraph bold data-sentry-element="Paragraph" data-sentry-source-file="IssueSummary.tsx">{issue.title}</Paragraph>
          <ParagraphSmall color="$textSubtle" data-sentry-element="ParagraphSmall" data-sentry-source-file="IssueSummary.tsx">{formattedDate}</ParagraphSmall>

          {hasActions(issue) ? (issue.actions ?? []).map((action: Action) => <OutlinedButton key={action.id} textProps={{
          marginRight: "$-2",
          color: "$textDefault",
          fontSize: "$2"
        }} height="$3" icon={() => <Circle backgroundColor="$red10" size={6} />} iconAfter={() => <ChevronRight size="$iconSmall" color="$iconSubtle" />} color="$textDefault" alignSelf="flex-start" onPress={handleActionPress(action.eventType)}
        // Typically we'd want to use our size tokens, but this spacing
        // is more a matter of shape and typographic balance than it is
        // of layout, per se.
        marginTop="$2.5" paddingRight={4} paddingLeft={14}>
                  {getActionLabel(action)}
                </OutlinedButton>) : null}
        </YStack>

        {hasAmounts(issue) ? <YStack alignItems="flex-end" gap="$1">
            {issue.dollarAmountSaved && Number(issue.dollarAmountSaved) > 0 ? <View backgroundColor="$limegreen" paddingHorizontal="$2" paddingVertical="$1" borderRadius="$8">
                <Paragraph2 bold color="$textDefault">
                  {`${CURRENCY_FORMATTER.format(Number(issue.dollarAmountSaved))} saved`}
                </Paragraph2>
              </View> : null}
            {issue.totalBillDollarAmount ? <XStack gap="$1.5" px="$2">
                {issue.dollarAmountSaved ? <ParagraphSmall theme="alt1">
                    {CURRENCY_FORMATTER.format(Number(issue.totalBillDollarAmount) - Number(issue.dollarAmountSaved ?? 0))}
                  </ParagraphSmall> : null}

                <ParagraphSmall color="$gray10" style={{
            textDecorationLine: "line-through"
          }}>
                  {/* make this the delta */}
                  {CURRENCY_FORMATTER.format(Number(issue.totalBillDollarAmount))}
                </ParagraphSmall>
              </XStack> : null}
          </YStack> : null}
      </XStack>
    </XStack>;
}